"use client";

import { useState, useEffect, useTransition } from "react";
import { useAuth } from "@clerk/nextjs";
import { useRouter, useSearchParams } from "next/navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CollectionIcon } from "@/components/collections/CollectionIcon";
import { StarButton } from "@/components/collections/StarButton";
import { CollaboratorAvatars } from "@/components/collections/CollaboratorAvatars";
import {
  Search,
  TrendingUp,
  Clock,
  Eye,
  GitFork,
  User,
  RotateCcw,
  LayoutGrid,
  HelpCircle,
} from "lucide-react";
import Link from "next/link";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import useSWR from "swr";
import { useDebouncedValue } from "@/hooks/use-debounced-value";

interface PublicCollection {
  id: string;
  name: string;
  description: string | null;
  icon: string | null;
  color: string | null;
  coverImage: string | null;
  itemCount: number;
  viewCount: number;
  cloneCount: number;
  starCount: number;
  hasStarred: boolean;
  createdAt: string;
  shareToken: string | null;
  memberCount?: number;
  members?: {
    user: {
      username: string | null;
      displayName: string | null;
      avatar: string | null;
    };
  }[];
  user: {
    username: string | null;
    displayName: string | null;
    avatar: string | null;
  };
}

type SortOption = "recent" | "popular" | "trending";

interface DiscoverResponse {
  ok: boolean;
  data: PublicCollection[];
  meta: {
    totalPages: number;
    currentPage: number;
    totalCount: number;
    limit: number;
  };
}

const fetcher = (url: string) => fetch(url).then((res) => res.json());

export function DiscoverPage() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [, startTransition] = useTransition();

  const [searchQuery, setSearchQuery] = useState(searchParams.get("q") || "");
  const debouncedSearchQuery = useDebouncedValue(searchQuery, 300);
  const [sortBy, setSortBy] = useState<SortOption>(
    (searchParams.get("sort") as SortOption) || "popular"
  );
  const [page, setPage] = useState(
    parseInt(searchParams.get("page") || "1", 10)
  );
  const [helpOpen, setHelpOpen] = useState(false);
  const { isSignedIn } = useAuth();

  // Sync URL with state
  useEffect(() => {
    const params = new URLSearchParams();
    if (debouncedSearchQuery) params.set("q", debouncedSearchQuery);
    if (sortBy !== "popular") params.set("sort", sortBy);
    if (page > 1) params.set("page", page.toString());

    const newUrl = `/discover${
      params.toString() ? "?" + params.toString() : ""
    }`;

    // Only update if URL actually changed to avoid infinite loops or unnecessary replacements
    const currentUrl = window.location.pathname + window.location.search;
    if (newUrl !== currentUrl) {
      startTransition(() => {
        router.replace(newUrl, { scroll: false });
      });
    }
  }, [debouncedSearchQuery, sortBy, page, router]);

  // Reset page when search or sort query changes
  useEffect(() => {
    // Only reset if the change wasn't from the initial URL load
    const urlQ = searchParams.get("q") || "";
    const urlSort = searchParams.get("sort") || "popular";

    if (debouncedSearchQuery !== urlQ || sortBy !== urlSort) {
      setPage(1);
    }
  }, [debouncedSearchQuery, sortBy, searchParams]);

  const params = new URLSearchParams({
    page: page.toString(),
    limit: "12",
    sort: sortBy,
    ...(debouncedSearchQuery && { q: debouncedSearchQuery }),
  });

  const {
    data: responseData,
    isLoading,
    isValidating,
    mutate,
  } = useSWR<DiscoverResponse>(`/api/collections/public?${params}`, fetcher, {
    revalidateOnFocus: false, // Don't revalidate when window gets focus
    dedupingInterval: 60000, // Cache for 1 minute
    keepPreviousData: true, // Keep showing previous data while fetching new page
  });

  const collections: PublicCollection[] =
    responseData?.ok && responseData?.data ? responseData.data : [];
  const totalPages = responseData?.ok ? responseData.meta.totalPages : 1;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
  };

  const handleRefresh = () => {
    mutate();
  };

  const handleStarUpdate = (collectionId: string, hasStarred: boolean) => {
    mutate(
      (currentData) => {
        if (!currentData || !currentData.ok) return currentData;

        const updatedCollections = currentData.data.map((c) => {
          if (c.id === collectionId) {
            return {
              ...c,
              hasStarred,
              starCount: hasStarred
                ? c.starCount + 1
                : Math.max(0, c.starCount - 1),
            };
          }
          return c;
        });

        return {
          ...currentData,
          data: updatedCollections,
        };
      },
      { revalidate: false }
    );
  };

  return (
    <div className="space-y-12 pb-20 selection:bg-zinc-800 selection:text-white">
      {/* Hero Header - Minimalist & Premium */}
      <div className="relative overflow-hidden p-10 sm:p-16 rounded-[3rem] bg-[#08080a] border border-white/[0.1] border-t-white/[0.15] shadow-2xl">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1593073862407-a3ce22748763?q=80&w=2274&auto=format&fit=crop&ixlib=rb-4.1.0&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
            alt=""
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#08080a] via-[#08080a]/70 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#08080a]/80 via-transparent to-transparent" />
        </div>
        <div className="relative z-10 max-w-2xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-white/[0.03] border border-white/[0.08] rounded-full mb-8">
            <TrendingUp className="w-3.5 h-3.5 text-zinc-400" />
            <span className="text-[13px] font-bold text-zinc-400 uppercase tracking-[0.1em]">
              Collective Intel
            </span>
          </div>
          <h1 className="text-5xl sm:text-6xl font-medium text-white tracking-[-0.03em] mb-6 leading-[1.1]">
            Global <span className="text-zinc-500">Discovery</span>
          </h1>
          <p className="text-lg text-zinc-500 leading-relaxed max-w-md font-light tracking-wide">
            Explore community archives. High-signal knowledge for modern
            collectors.
          </p>
          <button
            onClick={() => setHelpOpen(true)}
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/[0.06] border border-white/[0.1] text-zinc-400 hover:text-white hover:bg-white/[0.1] transition-all text-sm font-medium"
          >
            <HelpCircle className="w-4 h-4" />
            How it works
          </button>
        </div>
      </div>

      {/* Toolbar - Integrated & Clean */}
      <div className="sticky top-20 z-40 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-6 p-2 rounded-[1.5rem] bg-black/60 backdrop-blur-3xl border border-white/[0.12] border-t-white/[0.15] shadow-2xl">
        <div className="flex-1 flex items-center gap-2 pl-2">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-600" />
            <Input
              type="search"
              placeholder="Search archives..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 bg-transparent border-none focus-visible:ring-0 text-zinc-200 placeholder:text-zinc-600 font-light"
            />
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRefresh}
            className={`h-10 w-10 rounded-xl border border-white/[0.05] hover:bg-white/[0.05] hover:border-white/[0.12] transition-all ${
              isValidating ? "opacity-50" : ""
            }`}
          >
            <RotateCcw
              className={`h-4 w-4 text-zinc-500 ${
                isValidating ? "animate-spin" : ""
              }`}
            />
          </Button>
        </div>

        <div className="flex items-center gap-2 pr-1">
          <div className="hidden sm:block h-6 w-px bg-white/[0.1] mx-2" />
          <Select
            value={sortBy}
            onValueChange={(value: SortOption) => {
              setSortBy(value);
              setPage(1);
            }}
          >
            <SelectTrigger className="w-[140px] h-10 bg-transparent border-none focus:ring-0 text-zinc-400 font-medium hover:text-white transition-colors">
              <SelectValue placeholder="Sort" />
            </SelectTrigger>
            <SelectContent className="bg-[#0c0c0e] border-white/[0.1] text-zinc-300">
              <SelectItem value="popular">Popular</SelectItem>
              <SelectItem value="trending">Trending</SelectItem>
              <SelectItem value="recent">Newest</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Grid */}
      <div>
        {isLoading && !collections.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                className="h-72 rounded-[2.5rem] bg-white/[0.02] animate-pulse border border-white/[0.08]"
              />
            ))}
          </div>
        ) : !collections.length ? (
          <div className="text-center py-32 px-6 rounded-[3rem] bg-zinc-900/10 border border-white/[0.08]">
            <h3 className="text-xl font-medium text-zinc-400 mb-2">
              No Archives Found
            </h3>
            <p className="text-zinc-600 max-w-xs mx-auto mb-8 text-sm font-light">
              Your search did not return any results.
            </p>
          </div>
        ) : (
          <div className="space-y-16">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-10">
              {collections.map((collection) => (
                <CollectionCard
                  key={collection.id}
                  collection={collection}
                  onStarUpdate={handleStarUpdate}
                />
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-6 pt-12">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="text-zinc-500 hover:text-white rounded-full h-10 px-6 font-bold text-[13px] uppercase tracking-widest transition-all"
                >
                  Previous
                </Button>
                <div className="px-6 py-2 rounded-full bg-white/[0.02] border border-white/[0.08] text-[13px] font-black text-zinc-500 uppercase tracking-[0.1em]">
                  {page} / {totalPages}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page === totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  className="text-zinc-500 hover:text-white rounded-full h-10 px-6 font-bold text-[13px] uppercase tracking-widest transition-all"
                >
                  Next
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
      <Dialog open={helpOpen} onOpenChange={setHelpOpen}>
        <DialogContent className="bg-[#08080a] border-white/[0.1] sm:max-w-lg p-0 overflow-hidden rounded-3xl">
          <DialogHeader className="px-6 pt-6 pb-0">
            <DialogTitle className="text-white text-lg font-semibold">
              Getting Started with Discover
            </DialogTitle>
          </DialogHeader>
          <div className="px-6 pb-2">
            <p className="text-sm text-zinc-500 leading-relaxed">
              Browse public collections from the community. Star, clone, and get
              inspired by what others are curating.
            </p>
          </div>
          <div className="px-4 pb-4">
            <div className="rounded-2xl overflow-hidden border border-white/[0.08]">
              <video
                src="/video/discover-help.mp4"
                autoPlay
                loop
                muted
                playsInline
                className="w-full"
              />
            </div>
          </div>
          <ul className="px-6 pb-6 space-y-2 text-[12px] font-bold text-zinc-500 uppercase tracking-[0.1em]">
            <li className="flex items-center gap-2.5">
              <span className="w-1 h-1 rounded-full bg-[#5B5BFF] shrink-0" />
              Browse community collections
            </li>
            <li className="flex items-center gap-2.5">
              <span className="w-1 h-1 rounded-full bg-[#5B5BFF] shrink-0" />
              Star & clone collections you like
            </li>
            <li className="flex items-center gap-2.5">
              <span className="w-1 h-1 rounded-full bg-[#5B5BFF] shrink-0" />
              Search & sort by popularity or date
            </li>
          </ul>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CollectionCard({
  collection,
  onStarUpdate,
}: {
  collection: PublicCollection;
  onStarUpdate: (id: string, hasStarred: boolean) => void;
}) {
  const router = useRouter();
  const { isSignedIn } = useAuth();
  const creatorName =
    collection.user.displayName || collection.user.username || "Anonymous";

  const handleCardClick = () => {
    router.push(`/c/${collection.shareToken}`);
  };

  return (
    <div
      onClick={handleCardClick}
      className="group relative flex flex-col h-full rounded-[2.5rem] bg-[#08080a] border border-white/[0.12] border-t-white/[0.15] hover:border-white/[0.2] transition-all duration-700 cursor-pointer overflow-hidden shadow-2xl hover:shadow-[0_20px_50px_rgba(0,0,0,0.5)] hover:-translate-y-2"
    >
      {/* Hero Image Zone */}
      {collection.coverImage ? (
        <div className="relative h-40 w-full overflow-hidden flex-shrink-0">
          <img
            src={collection.coverImage}
            alt={collection.name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
          />
          {/* Bottom fade into card background */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#08080a] via-transparent to-transparent" />
        </div>
      ) : (
        /* No image fallback — accent line */
        <div className="relative h-2 w-full flex-shrink-0">
          <div
            className="absolute top-0 left-0 right-0 h-[1px] opacity-30 group-hover:opacity-100 transition-opacity duration-700"
            style={{
              background: `linear-gradient(to right, transparent, ${
                collection.color || "#fff"
              }, transparent)`,
            }}
          />
        </div>
      )}

      {/* Content Zone */}
      <div className="relative z-10 px-8 pb-8 pt-2 flex flex-col flex-1">
        {/* Icon - overlaps image/content boundary */}
        <div
          className={`flex items-start justify-between ${
            collection.coverImage ? "-mt-8" : "mt-4"
          } mb-6`}
        >
          <div className="p-3.5 rounded-2xl bg-[#111113] border border-white/[0.12] group-hover:border-white/[0.2] transition-all duration-700 shadow-lg">
            <CollectionIcon
              icon={collection.icon}
              color={collection.color}
              size="lg"
            />
          </div>
        </div>

        <div className="flex-1 min-w-0">
          <h3 className="text-xl font-bold text-white group-hover:text-zinc-100 transition-colors tracking-tight mb-2 leading-tight">
            {collection.name}
          </h3>
          {collection.description && (
            <p className="text-sm text-zinc-500 line-clamp-2 leading-relaxed mb-6 font-light tracking-wide">
              {collection.description}
            </p>
          )}
        </div>

        <div className="mt-auto space-y-5 pt-5 border-t border-white/[0.08]">
          {/* Social Proof Row */}
          <div className="flex items-center justify-between">
            <div className="text-[13px] font-bold text-zinc-500 uppercase tracking-[0.1em]">
              {collection.itemCount} Objects
            </div>
            {(collection.memberCount ?? 0) > 0 && (
              <CollaboratorAvatars
                memberCount={collection.memberCount ?? 0}
                members={collection.members}
              />
            )}
          </div>

          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 group/creator min-w-0">
              <div className="w-7 h-7 rounded-full bg-zinc-900 border border-white/[0.1] flex items-center justify-center flex-shrink-0 group-hover/creator:border-white/[0.3] transition-colors overflow-hidden">
                {collection.user.avatar ? (
                  <img
                    src={collection.user.avatar}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <User className="h-3.5 w-3.5 text-zinc-600" />
                )}
              </div>
              <span className="text-[12px] font-bold text-zinc-500 group-hover/creator:text-zinc-300 transition-colors truncate uppercase tracking-widest">
                {creatorName}
              </span>
            </div>

            <div className="flex items-center gap-4 text-zinc-600">
              <div className="flex items-center gap-1.5" title="Stars">
                <StarButton
                  collectionId={collection.id}
                  initialStarCount={collection.starCount}
                  initialHasStarred={collection.hasStarred}
                  variant="ghost"
                  size="sm"
                  showCount={true}
                  isAuthenticated={!!isSignedIn}
                  onStarChange={(starred) => onStarUpdate(collection.id, starred)}
                  className="h-auto p-0 hover:bg-transparent text-zinc-600 hover:text-white transition-colors"
                />
              </div>
              <div className="flex items-center gap-1.5" title="Total Views">
                <Eye className="h-3.5 w-3.5 opacity-50" />
                <span className="text-[12px] font-medium">
                  {collection.viewCount}
                </span>
              </div>
              <div className="flex items-center gap-1.5" title="Clones">
                <GitFork className="h-3.5 w-3.5 opacity-50" />
                <span className="text-[12px] font-medium">
                  {collection.cloneCount}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
